import { useEffect, useRef, useState } from 'react';
import { api, readJson, SERVER_UNREACHABLE } from '../lib/api.ts';
import { attachBuildProgress } from '../lib/buildProgress.ts';
import { rememberBuild, recallBuild, forgetBuild } from '../lib/activeBuild.ts';
import { confirmStagingSpend, stagingAckHeaders } from '../lib/stagingGuard.ts';
import { errorMessage } from '../lib/api.ts';
import type { PlanOutput } from '../../shared/deck.ts';
import type { BuildEvent, SectionEngine } from '../../shared/api.ts';

export type SectionBuildDeps = {
  slug: string;
  refreshSummary?: () => void;
  onSectionCreated?: (sectionKey: string | null) => void;
};

/** intake (form) → planning → review (plan gate) → building → done|failed. */
export type SectionBuildPhase = 'intake' | 'planning' | 'review' | 'building' | 'failed';

/** The section being built, as the form describes it. */
export type SectionDraft = { key: string; [field: string]: unknown };

/** Events the section-build stream sends: the build registry's own `BuildEvent`,
 *  since this is the same `/api/wizard/build-progress` stream the new-deck
 *  wizard reads. It used to be a hand-written union here, which had already
 *  drifted from what the server emits. */
export type SectionBuildEvent = BuildEvent;

/**
 * Lifecycle for building ONE new section into the OPEN deck from uploaded
 * content, with a plan-review gate. A trimmed, in-editor sibling of
 * NewDeckAgent's build lifecycle (that one runs on the /new route; this runs
 * inside the deck editor). Reuses the server's build-progress SSE and
 * build-cancel endpoints; the section plan lives on the job (build-section-plan
 * / build-section-resume).
 *
 * Phases: intake (form) → planning → review (plan gate) → building → done|failed.
 * `slide` events refresh the deck summary so slides stream into the filmstrip.
 */
export function useSectionBuild({ slug, refreshSummary, onSectionCreated }: SectionBuildDeps) {
  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState<SectionBuildPhase>('intake');
  const [plan, setPlan] = useState<PlanOutput | null>(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [planErrors, setPlanErrors] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Mirrors for the SSE reader, which closes over a stale render.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const jobRef = useRef<{ jobId: string } | null>(null);
  const sectionKeyRef = useRef<string | null>(null);
  // The live progress attachment, so it can be torn down. Without this the
  // reconnect recursion outlived the component: navigating away from a deck
  // mid-build left it fetching and calling setState on a dead component for
  // the life of the page (NewDeckAgent already cleaned up; this copy didn't).
  const attachRef = useRef<{ detach: () => void } | null>(null);
  useEffect(() => () => attachRef.current?.detach(), []);

  // Re-attach to a section build this browser was already watching. The slides
  // land server-side regardless, but the filmstrip only refreshes on this
  // build's own `slide` events — so without re-attaching, a refresh mid-build
  // left the deck looking static while slides were still arriving.
  useEffect(() => {
    if (!slug) return;
    const remembered = recallBuild(`section:${slug}`);
    if (!remembered) return;
    jobRef.current = { jobId: remembered.jobId };
    setIsOpen(false); // the modal is unmounted during 'building' anyway
    setP('building');
    attachProgress(remembered.jobId, { resumed: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  const setP = (p: SectionBuildPhase) => {
    phaseRef.current = p;
    setPhase(p);
  };

  function reset() {
    setP('intake');
    setPlan(null);
    setPlanErrors(null);
    setPlanBusy(false);
    setError(null);
    jobRef.current = null;
    sectionKeyRef.current = null;
  }
  const open = () => {
    reset();
    setIsOpen(true);
  };
  const close = () => {
    setIsOpen(false);
    reset();
  };

  async function startBuild(
    section: SectionDraft,
    answers: Record<string, unknown> | null,
    docs: File[] | null,
    imgs: File[] | null,
    guidance?: string,
    engine?: SectionEngine,
  ) {
    if (!(await confirmStagingSpend('building this section'))) return;
    sectionKeyRef.current = section.key;
    setP('planning');
    setError(null);
    const form = new FormData();
    form.append('deckSlug', slug);
    form.append('section', JSON.stringify(section));
    form.append('answers', JSON.stringify(answers || {}));
    if (guidance && guidance.trim()) form.append('guidance', guidance.trim());
    // Only the non-default engines name themselves, as the deck build does — an
    // older client sends nothing and the server reads that as Claude.
    if (engine && engine !== 'claude') form.append('engine', engine);
    for (const f of [...(docs || []), ...(imgs || [])]) form.append('file', f);
    try {
      let res;
      try {
        res = await fetch('/api/wizard/build-section', {
          method: 'POST',
          credentials: 'same-origin',
          headers: await stagingAckHeaders(),
          body: form,
        });
      } catch {
        // Never left the browser — the studio is down, not the build.
        throw new Error(SERVER_UNREACHABLE);
      }
      // Check the status BEFORE parsing — a non-JSON error body (e.g. a bare
      // "404 Not Found" from a stale server) must surface the real status, not
      // a cryptic JSON.parse error.
      if (!res.ok) {
        const data = await readJson<{ error?: string }>(res);
        if (data?.error) throw new Error(data.error);
        throw new Error(res.status >= 500 ? SERVER_UNREACHABLE : `Build failed (${res.status})`);
      }
      // Same rule on the way out: a 200 that isn't JSON is still not a job.
      const data = await readJson<{ jobId?: string }>(res);
      if (!data?.jobId) throw new Error(SERVER_UNREACHABLE);
      const j = { jobId: data.jobId };
      jobRef.current = j;
      // Survive a refresh: the slides keep landing server-side either way, but
      // without this the filmstrip stops updating (it only refreshes on the
      // build's own `slide` events) until a manual reload.
      rememberBuild(`section:${slug}`, { jobId: data.jobId, slug });
      attachProgress(data.jobId);
    } catch (err) {
      setError(errorMessage(err));
      setP('failed');
    }
  }

  // SSE attach with bounded auto-reconnect — the job outlives any one
  // connection, but a job that is GONE (server restarted, or reaped) must end
  // the wait instead of retrying forever behind a spinner. See buildProgress.js.
  function attachProgress(jobId: string, { resumed = false }: { resumed?: boolean } = {}) {
    attachRef.current?.detach();
    attachRef.current = attachBuildProgress({
      jobId,
      onEvent: handleEvent,
      isCurrent: () => jobRef.current?.jobId === jobId,
      onEnd: (reason) => {
        if (reason === 'done') return;
        if (jobRef.current?.jobId !== jobId) return;
        jobRef.current = null;
        forgetBuild(`section:${slug}`);
        refreshSummary?.();
        // A RESUMED job that is gone finished a while ago and was reaped —
        // the ordinary case. Going quiet beats toasting a failure every time
        // the deck is opened with a stale record.
        if (resumed && reason === 'gone') {
          setP('intake');
          return;
        }
        setError(
          reason === 'gone'
            ? 'Lost track of this build: the server restarted. Any slides it finished are in the deck.'
            : "Lost the connection to this build and couldn't get it back. Any slides it finished are in the deck.",
        );
        setP('failed');
      },
    });
  }

  /** Returns true when the stream is finished (done event seen). */
  function handleEvent(frame: unknown): boolean {
    // Asserted once, as in chatTurns: this is our own server's stream.
    const evt = frame as SectionBuildEvent;
    // Abandoned (cancelled/closed): ignore trailing events and end the stream.
    if (phaseRef.current === 'intake') return true;
    switch (evt.type) {
      case 'plan':
        setPlan(evt.plan);
        setPlanBusy(false);
        setP('review');
        return false;
      case 'slide':
        // A slide was registered in the manifest — pull it into the filmstrip.
        refreshSummary?.();
        return false;
      case 'phase':
        if (evt.phase === 'building' && (phaseRef.current === 'planning' || phaseRef.current === 'review')) {
          setP('building');
        }
        return false;
      case 'error':
        setError(evt.message);
        return false;
      case 'done':
        jobRef.current = null;
        forgetBuild(`section:${slug}`);
        refreshSummary?.();
        if (evt.code === 0) {
          onSectionCreated?.(sectionKeyRef.current);
          close();
        } else {
          setP('failed');
        }
        return true;
      default:
        return false;
    }
  }

  /** Approve the (edited) plan: save the slides, then resume into the write turn. */
  async function approvePlan(editedSlides: unknown) {
    const job = jobRef.current;
    if (!job) return;
    setPlanBusy(true);
    setPlanErrors(null);
    try {
      const res = await fetch(`/api/wizard/build-section-plan/${job.jobId}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slides: editedSlides }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPlanErrors(data?.errors || [data?.error || `${res.status}`]);
        setPlanBusy(false);
        return;
      }
      await api.post(`/api/wizard/build-section-resume/${job.jobId}`, {});
      setP('building');
      setPlanBusy(false);
    } catch (err) {
      setPlanErrors([errorMessage(err)]);
      setPlanBusy(false);
    }
  }

  /** Cancel the job (if running) and close the modal. Also used as the review
   *  screen's "Back" and the modal's ✕. */
  async function cancel() {
    const job = jobRef.current;
    jobRef.current = null; // stop the SSE reader reconnecting to the cancelled job
    forgetBuild(`section:${slug}`);
    attachRef.current?.detach();
    attachRef.current = null;
    if (job) {
      try {
        await api.post(`/api/wizard/build-cancel/${job.jobId}`, {});
      } catch (err) {
        // Don't claim a cancel we never landed: closing the modal while the
        // agent keeps writing slides into the open deck is the silent
        // divergence this pass exists to remove. Route it through the normal
        // failure path so Canvas toasts it (and closes for us).
        setError(`Couldn't stop the section build: ${errorMessage(err)}. It may still be running.`);
        setP('failed');
        return;
      }
    }
    close();
  }

  return { isOpen, open, close, startBuild, phase, plan, planBusy, planErrors, error, approvePlan, cancel };
}
